import { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { RefreshCcw } from "lucide-react"
import Card from "../components/Card"
import Sparkline from "../components/Sparkline"
import StatusPill from "../components/StatusPill"
import { cx } from "../lib/cx"
import type { Kpi } from "../types"

type DashboardData = {
  kpis: Kpi[]
  billingHub: Array<{
    client: string
    project: string
    amount: string
    status: string
    due: string
  }>
  timeSummary: {
    hoursLogged: number
    hoursBillable: number
    unbilledHours: number
  }
  teamSnapshot: {
    role: string
    activeProjects: number
    hoursThisWeek: number
    paidHours: number
    unpaidHours: number
    earnings: string
  }
  timelineMetrics: Array<{ label: string; value: string }>
  timelineSeries: number[]
  // Per-metric series for the toggle chart
  metricSeries?: Record<string, number[]>
  // Computed earnings data
  earnings?: {
    totalRevenue: number
    totalExpenses: number
    profitMargin: number
    outstanding: number
    paidRatio: number
    unpaidRatio: number
  }
}

const toggleOptions = ["Revenue", "Expenses", "Profit", "Hours worked", "Client growth"]

const buildLinePath = (values: number[], width: number, height: number) => {
  if (!values || values.length === 0) return ""
  const max = Math.max(...values)
  const min = Math.min(...values)
  const range = max - min || 1

  return values
    .map((value, index) => {
      const x = (index / (values.length - 1)) * width
      const y = height - ((value - min) / range) * height
      return `${index === 0 ? "M" : "L"}${x},${y}`
    })
    .join(" ")
}

const emptyEarnings = {
  totalRevenue: 0,
  totalExpenses: 0,
  profitMargin: 0,
  outstanding: 0,
  paidRatio: 0,
  unpaidRatio: 0,
}

const DashboardPage = () => {
  const navigate = useNavigate()
  const [activeMetric, setActiveMetric] = useState(toggleOptions[0])
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  const fetchData = async () => {
    try {
      const res = await fetch("/api/dashboard")
      if (!res.ok) throw new Error("API error")
      const json = await res.json() as DashboardData
      setData({
        ...json,
        metricSeries: json.metricSeries || {},
        earnings: json.earnings || emptyEarnings,
      })
    } catch {
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Trigger Stripe invoice sync in background (fire-and-forget), then load dashboard
    fetch("/api/cron/sync-stripe", { method: "POST" }).catch(() => {})
    fetchData()
  }, [])

  const [refreshError, setRefreshError] = useState(false)

  const handleRefresh = async () => {
    setRefreshing(true)
    setRefreshError(false)
    try {
      await fetch("/api/cron/sync-stripe", { method: "POST" }).catch(() => {})
      await fetch("/api/cron/refresh-stats", { method: "POST" })
      await fetchData()
    } catch {
      setRefreshError(true)
      setTimeout(() => setRefreshError(false), 3000)
    } finally {
      setRefreshing(false)
    }
  }

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <p className="text-sm text-stone">Loading dashboard...</p>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex h-96 items-center justify-center">
        <p className="text-sm text-stone">Unable to load dashboard data.</p>
      </div>
    )
  }

  const { kpis, billingHub, timeSummary, teamSnapshot, timelineMetrics, metricSeries, earnings } = data

  // Use the selected metric's series for the chart, falling back to the default
  const currentSeries = metricSeries?.[activeMetric] || data.timelineSeries
  const path = buildLinePath(currentSeries, 520, 180)

  // Chart end point for the dot
  const endY =
    currentSeries.length > 0
      ? (() => {
          const max = Math.max(...currentSeries)
          const min = Math.min(...currentSeries)
          const range = max - min || 1
          return 180 - ((currentSeries[currentSeries.length - 1] - min) / range) * 180
        })()
      : 30

  // Earnings computed values
  const earningsData = earnings || emptyEarnings
  const earningsPercent = Math.round(
    (earningsData.totalRevenue / (earningsData.totalRevenue + earningsData.totalExpenses || 1)) * 100,
  )
  const earningsDeg = Math.round((earningsPercent / 100) * 360)

  // Profit status
  const profitValue = (timelineMetrics.find((m) => m.label === "Profit")?.value) || "$0"
  const isOnTrack = parseFloat(profitValue.replace(/[^0-9.-]/g, "")) > 0

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header Actions */}
      <div className="flex justify-end">
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-stone shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCcw className={cx("h-3 w-3", refreshing && "animate-spin")} />
          {refreshing ? "Refreshing..." : refreshError ? "Refresh failed" : "Refresh Data"}
        </button>
      </div>

      <div className="grid grid-cols-12 gap-4 sm:gap-6">
        {kpis.map((kpi) => (
          <Link
            key={kpi.id}
            to={kpi.href ?? "/reports"}
            className="col-span-12 block sm:col-span-6 lg:col-span-3 xl:col-span-3"
          >
            <Card className="h-full transition hover:shadow-card">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-stone">{kpi.label}</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-900">{kpi.value}</p>
                  <p className="mt-1 text-xs text-sage">
                    {kpi.delta} · {kpi.caption}
                  </p>
                </div>
                <Sparkline data={kpi.trend} className="text-sage" />
              </div>
            </Card>
          </Link>
        ))}

        <Card className="col-span-12 lg:col-span-8 xl:col-span-7 2xl:col-span-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-base sm:text-lg font-semibold text-slate-900">Agency Financial Timeline</p>
              <p className="text-xs sm:text-sm text-stone">
                Revenue, expenses, profit, hours, and client growth.
              </p>
            </div>
            <StatusPill
              tone={isOnTrack ? "success" : "warning"}
              label={isOnTrack ? "On track" : "Needs attention"}
            />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {toggleOptions.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setActiveMetric(option)}
                className={cx(
                  "rounded-full px-2.5 sm:px-3 py-1 text-xs font-semibold transition",
                  activeMetric === option
                    ? "bg-moss text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200",
                )}
              >
                {option}
              </button>
            ))}
          </div>
          <div className="mt-5 rounded-[20px] border border-line bg-gradient-to-br from-white via-white to-slate-50 p-3 sm:p-4">
            <svg width="100%" height="180" viewBox="0 0 520 180" fill="none" className="w-full">
              <path d={path} stroke="#6B8F7A" strokeWidth="3" strokeLinecap="round" />
              <circle cx="520" cy={endY} r="5" fill="#6B8F7A" />
            </svg>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-5">
            {timelineMetrics.map((metric) => (
              <div key={metric.label}>
                <p className="text-xs uppercase tracking-wide text-stone">{metric.label}</p>
                <p className="text-sm font-semibold text-slate-900">{metric.value}</p>
              </div>
            ))}
          </div>
        </Card>

        <div className="col-span-12 lg:col-span-4 xl:col-span-5 2xl:col-span-4 space-y-4 sm:space-y-6">
          <Card>
            <div className="flex items-center justify-between">
              <p className="text-lg font-semibold text-slate-900">Earnings</p>
              <StatusPill
                tone={earningsData.profitMargin > 20 ? "success" : "warning"}
                label={`+${earningsData.profitMargin}% vs last month`}
              />
            </div>
            <p className="mt-2 text-sm text-stone">
              Total expenses · ${earningsData.totalExpenses.toLocaleString()}
            </p>
            <div className="mt-6 flex items-center justify-between">
              <div className="relative h-32 w-32">
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: `conic-gradient(#6B8F7A 0deg ${earningsDeg}deg, #E7E5E4 ${earningsDeg}deg 360deg)`,
                  }}
                />
                <div className="absolute inset-4 flex items-center justify-center rounded-full bg-white text-xl font-semibold text-slate-900">
                  {earningsPercent}%
                </div>
              </div>
              <div className="space-y-2 text-sm text-stone">
                <p>Profit margin: {earningsData.profitMargin}%</p>
                <p>Outstanding: ${earningsData.outstanding.toLocaleString()}</p>
                <p>Paid vs unpaid: {earningsData.paidRatio}% / {earningsData.unpaidRatio}%</p>
              </div>
            </div>
          </Card>
          <Card>
            <p className="text-lg font-semibold text-slate-900">Team Snapshot</p>
            <p className="text-sm text-stone">
              {teamSnapshot.role} · Active projects {teamSnapshot.activeProjects}
            </p>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-stone">Hours this week</p>
                <p className="text-base font-semibold text-slate-900">
                  {teamSnapshot.hoursThisWeek}
                </p>
              </div>
              <div>
                <p className="text-xs text-stone">Earnings this month</p>
                <p className="text-base font-semibold text-slate-900">{teamSnapshot.earnings}</p>
              </div>
              <div>
                <p className="text-xs text-stone">Paid hours</p>
                <p className="text-base font-semibold text-slate-900">
                  {teamSnapshot.paidHours}
                </p>
              </div>
              <div>
                <p className="text-xs text-stone">Unpaid hours</p>
                <p className="text-base font-semibold text-slate-900">
                  {teamSnapshot.unpaidHours}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate("/reports")}
              className="mt-4 w-full rounded-full bg-moss px-4 py-2 text-sm font-semibold text-white shadow-soft transition hover:bg-moss/90"
            >
              View performance breakdown
            </button>
          </Card>
        </div>

        <Card className="col-span-12 lg:col-span-6 xl:col-span-6 2xl:col-span-4">
          <div className="flex items-center justify-between">
            <p className="text-base sm:text-lg font-semibold text-slate-900">Client Billing Hub</p>
            <StatusPill
              tone="warning"
              label={`${billingHub.filter((i) => i.status === "Draft" || i.status === "Sent").length} pending`}
            />
          </div>
          <p className="text-xs sm:text-sm text-stone">
            Hourly line items, retainers, flat fees, and synced payments.
          </p>
          <div className="mt-4 space-y-3">
            {billingHub.map((item) => (
              <div
                key={`${item.client}-${item.project}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-line bg-white px-3 sm:px-4 py-3 text-sm"
              >
                <div className="flex-1 min-w-[120px]">
                  <p className="font-semibold text-slate-900">{item.client}</p>
                  <p className="text-xs text-stone">{item.project}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-slate-900">{item.amount}</p>
                  <p className="text-xs text-stone">Due {item.due}</p>
                </div>
                <StatusPill
                  tone={
                    item.status === "Paid"
                      ? "success"
                      : item.status === "Overdue"
                        ? "danger"
                        : "warning"
                  }
                  label={item.status}
                />
              </div>
            ))}
          </div>
          <Link
            to="/billing"
            className="mt-4 inline-flex items-center rounded-full bg-slate-100 px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
          >
            Open billing workspace
          </Link>
        </Card>

        <Card className="col-span-12 sm:col-span-6 lg:col-span-3 xl:col-span-3 2xl:col-span-4">
          <p className="text-base sm:text-lg font-semibold text-slate-900">Time Tracking Pulse</p>
          <p className="text-xs sm:text-sm text-stone">Convert hours to invoices in one click.</p>
          <div className="mt-4 space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-stone">Hours logged</span>
              <span className="font-semibold text-slate-900">{timeSummary.hoursLogged}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-stone">Billable hours</span>
              <span className="font-semibold text-slate-900">{timeSummary.hoursBillable}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-stone">Unbilled hours</span>
              <span className="font-semibold text-slate-900">{timeSummary.unbilledHours}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate("/billing")}
            className="mt-5 w-full rounded-full bg-moss px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-white transition hover:bg-moss/90"
          >
            Convert to invoice
          </button>
        </Card>

        <Card className="col-span-12 sm:col-span-6 lg:col-span-3 xl:col-span-3 2xl:col-span-4">
          <p className="text-base sm:text-lg font-semibold text-slate-900">Quick Actions</p>
          <p className="text-xs sm:text-sm text-stone">Jump to common tasks.</p>
          <div className="mt-4 space-y-2">
            <Link
              to="/time"
              className="block w-full rounded-[18px] border border-line bg-slate-50 px-3 sm:px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
            >
              Start time tracker
            </Link>
            <Link
              to="/clients"
              className="block w-full rounded-[18px] border border-line bg-slate-50 px-3 sm:px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
            >
              Add new client
            </Link>
            <Link
              to="/tasks"
              className="block w-full rounded-[18px] border border-line bg-slate-50 px-3 sm:px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
            >
              View my tasks
            </Link>
            <Link
              to="/settings"
              className="block w-full rounded-[18px] border border-line bg-slate-50 px-3 sm:px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
            >
              Security settings
            </Link>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default DashboardPage
